import type { ButtonProps } from '@chakra-ui/react'
import type { FC } from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RRule } from 'rrule'

import { Button } from '@app/components/ui/button'
import { toaster } from '@app/components/ui/toaster'
import { useStrategyMapContext } from '@app/pages/strategies/strategyMapContext'
import {
  RecurringReportConfigurationServiceEnum,
  useRecurringReportConfigurationCreateMutation
} from '@graphql/queries'

interface Props extends ButtonProps {
  strategyId: string
}

const AiDashboardReportAddNew: FC<Props> = ({ strategyId, ...rest }) => {
  const { canEdit } = useStrategyMapContext()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  const [, create] = useRecurringReportConfigurationCreateMutation()

  // Use RRule to generate a new schedule string, required for creating a new report
  const schedule = new RRule({ freq: RRule.WEEKLY, interval: 1 }).toString()

  const handleAddNew = async () => {
    setLoading(true)

    await create({
      input: {
        strategyId,
        name: 'Untitled Report',
        service: RecurringReportConfigurationServiceEnum.EmitReport,
        schedule
      }
    })
      .then((res) => {
        const newRecurringReport = res?.data?.recurringReportConfigurationCreate?.recurringReportConfiguration

        if (newRecurringReport) {
          navigate(`/strategy/${strategyId}/dashboard/${newRecurringReport.id}/current`)
        } else {
          toaster.create({
            type: 'error',
            title: 'Report',
            description: 'Failed to create a new report.'
          })
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  if (!canEdit) {
    return null
  }

  return (
    <Button w="full" loading={loading} onClick={handleAddNew} {...rest}>
      Add a new report
    </Button>
  )
}

export default AiDashboardReportAddNew
